import React, { useCallback } from 'react';
import { useObservable } from 'react-use-observable';

import { Pagination, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDate } from 'helpers/date';
import Truncate from 'v2/components/Shared/TruncateText';
import WhatsAppLink from 'v2/components/Shared/WhatsAppLink';
import leadsService from 'v2/services/leads/list';
import tableService from 'v2/services/table';

import { Icon } from '@eduzz/ui-antd-theme';

const columns: ColumnsType<any> = [
  {
    title: 'Data',
    dataIndex: 'created_at',
    width: 120,
    render: (text: string) => (
      <div>
        <Typography.Text className='block'>{text ? formatDate(text, 'dd/MM/yyyy') : '-'}</Typography.Text>
        <Typography.Text className='block' type='secondary'>
          {text ? formatDate(text, 'HH:mm') : '-'}
        </Typography.Text>
      </div>
    )
  },
  {
    title: 'Nome',
    render: (_: any, record: any) => <Typography.Text>{record.customer?.name ?? '-'}</Typography.Text>
  },
  {
    title: 'E-mail',
    dataIndex: 'email',
    width: 250,
    render: (text: string) => (
      <Typography.Text>
        <Truncate count={30} final='...'>
          {text}
        </Truncate>
      </Typography.Text>
    )
  },
  {
    title: 'Telefone',
    width: 120,
    render: (_: any, record: any) =>
      record?.customer?.cellphone ? <WhatsAppLink phone={record.customer.cellphone} /> : record.customer?.telephone
  },
  {
    title: 'Campanha',
    width: 140,
    render: (_: any, record: any) => <Typography.Text>{record.campaign?.key ?? '-'}</Typography.Text>
  },
  {
    title: 'Produto',
    render: (_: any, record: any) => (
      <div className='flex flex-col items-start gap-1'>
        <Typography.Text>
          <Truncate count={30} final='...'>
            {record?.product?.title ?? '-'}
          </Truncate>
        </Typography.Text>
        <Typography.Text type='secondary'>{record?.product?.id ? `(${record?.product?.id})` : '-'}</Typography.Text>
      </div>
    )
  },
  {
    title: 'Afiliado',
    width: 175,
    render: (_: any, record: any) => <Typography.Text>{record.affiliate?.name ?? '-'}</Typography.Text>
  },
  {
    title: 'Fatura',
    width: 100,
    render: (_: any, record: any) => (
      <>
        {record.abandonment && !record.invoice_id && (
          <Tooltip placement='left' title='Abandono de carrinho'>
            <Icon.ShoppingCart />
          </Tooltip>
        )}
        {!record.abandonment && record.invoice_id && (
          <Typography.Text type='secondary'>{record.invoice_id}</Typography.Text>
        )}
      </>
    )
  }
];

const List = () => {
  useObservable(() => leadsService.load(), []);

  const [isLoading] = useObservable(() => tableService.isLoading(), []);
  const [leads] = useObservable(() => leadsService.getLeads(), []);
  const [pagination] = useObservable(() => leadsService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (perPage: number) => {
      leadsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      leadsService.setPagination({ ...pagination, current_page: page });
    },
    [pagination]
  );

  return (
    <>
      <Table
        id='leads_table'
        size='small'
        sticky
        className='[&_.ant-table-tbody_.ant-typography]:text-sm'
        dataSource={leads || []}
        scroll={{ x: 'max-content' }}
        columns={columns}
        rowKey='id'
        loading={isLoading}
        pagination={false}
      />
      {!isLoading && pagination && (
        <div className='mt-4 flex justify-end'>
          <Pagination
            current={pagination.current_page || 1}
            pageSize={pagination.per_page || 10}
            total={pagination.total_items || 0}
            pageSizeOptions={[10, 20, 50, 100, 200, 500]}
            showSizeChanger
            onChange={onChangePage}
            onShowSizeChange={(_, size) => onChangePerPage(size)}
          />
        </div>
      )}
    </>
  );
};
export default List;
